import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { ApiService } from '../api/api.service';
import { Gift, GiftFilters, GiftPrintBannerImageOption, GiftStatus } from '../../models/gift';
import {
  GiftableProduct,
  GiftCenterCarouselImage,
  GiftCenterFeaturedGift,
  GiftCenterHomePreview,
  GiftCenterHomePreviewPayload,
  GiftProductPresentation,
  GiftProductPresentationWrite,
  GiftProductTarget,
} from '../../models/gift-center';
import { ListResponse } from '../../models/list-response';


@Injectable({
  providedIn: 'root'
})
export class GiftService {
  constructor(
    private apiService: ApiService
  ) {}

  getGifts(pageNumber?: number,
           pageSize?: number,
           filters?: GiftFilters): Promise<ListResponse<Gift>> {
    return this.apiService.getCollection<Gift, GiftFilters>(
      'gifts/',
      pageNumber,
      pageSize,
      filters,
      true
    );
  }

  getGift(id: number): Promise<Gift> {
    return this.apiService.getItem<Gift>(
      'gifts/',
      id
    );
  }

  changeGiftExpirationDate(giftId: number,
                           newExpirationDate: string) {
    return this.apiService.postToAPI<any>(
      'gifts/' + giftId.toString() + '/change-expiration-date/',
      {
        expiration_date: newExpirationDate
      }
    ).then();
  }

  expireGiftImmediately(giftId: number,
                        willRefund: boolean) {
    return this.apiService.postToAPI<any>(
      'gifts/' + giftId.toString() + '/expire/',
      {
        refund_policy: willRefund ? 'refund' : 'no_refund'
      }
    ).then();
  }

  getGiftCardBalance(giftId: number): Promise<{ balance: string; status: GiftStatus }> {
    return this.apiService.getFromAPI<{ balance: string; status: GiftStatus }>(
      'gifts/' + giftId.toString() + '/gift-card-balance/'
    );
  }

  downloadGiftPDF(giftId: number,
                  bannerImageOptionId: number | null): Promise<void> {
    let apiPath = 'gifts/' + giftId.toString() + '/get-pdf/'
    if (bannerImageOptionId) {
      apiPath += '?banner_image_option_id=' + bannerImageOptionId.toString();
    }
    return this.apiService.downloadFromApi(
      apiPath,
      'gift-' + giftId.toString() + '.pdf'
    );
  }

  getGiftBannerImageOptions(pageNumber?: number,
                            pageSize?: number): Promise<ListResponse<GiftPrintBannerImageOption>> {
    return this.apiService.getCollectionNoFilters<GiftPrintBannerImageOption>(
      'gift-print-banner-image-options/',
      pageNumber,
      pageSize
    );
  }

  getGiftBannerImageOption(id: number): Promise<GiftPrintBannerImageOption> {
    return this.apiService.getItem<GiftPrintBannerImageOption>(
      'gift-print-banner-image-options/',
      id
    );
  }

  createGiftBannerImageOption(opt: GiftPrintBannerImageOption,
                              bannerImageFileData: File | null): Promise<GiftPrintBannerImageOption> {
    let optPromise = this.apiService
      .postToAPI<GiftPrintBannerImageOption>(
        'gift-print-banner-image-options/',
        {
          name: opt.name
        }
      );

    if (bannerImageFileData !== null) {
      optPromise = optPromise.then(createdOpt => {
        const bannerImageFormData = new FormData();
        bannerImageFormData.append(
          'image',
          bannerImageFileData,
          bannerImageFileData.name
        );
        return this.apiService.patchToAPI<GiftPrintBannerImageOption>(
            createdOpt.url,
            bannerImageFormData,
            true
          );
      });
    }

    return optPromise;
  }

  updateGiftBannerImageOption(opt: GiftPrintBannerImageOption,
                              bannerImageFileData: File | null): Promise<GiftPrintBannerImageOption> {
    const patchBody: any = {
      name: opt.name
    };
    if ((opt.image === null) && (bannerImageFileData === null)) {
      patchBody.image = null;
    }
    let optPromise = this.apiService
      .patchToAPI<GiftPrintBannerImageOption>(opt.url, patchBody);

    if (bannerImageFileData !== null) {
      optPromise = optPromise.then(updatedOpt => {
        const bannerImageFormData = new FormData();
        bannerImageFormData.append(
          'image',
          bannerImageFileData,
          bannerImageFileData.name
        );
        return this.apiService
          .patchToAPI<GiftPrintBannerImageOption>(
            updatedOpt.url,
            bannerImageFormData,
            true
          );
      });
    }

    return optPromise;
  }

  removeGiftBannerImageOption(optUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      optUrl
    ).then();
  }

  getGiftCenterGiftableProducts(): Promise<GiftableProduct[]> {
    return this.apiService.getFromAPI<GiftableProduct[]>(
      'gift-center-giftable-products/'
    );
  }

  getGiftCenterCarouselImages(): Promise<GiftCenterCarouselImage[]> {
    return this.apiService.getFromAPI<GiftCenterCarouselImage[]>(
      'gift-center-carousel-images/'
    );
  }

  createGiftCenterCarouselImage(formData: FormData): Promise<GiftCenterCarouselImage> {
    return this.apiService.postToAPI<GiftCenterCarouselImage>(
      'gift-center-carousel-images/',
      formData,
      false,
      false,
      true
    );
  }

  updateGiftCenterCarouselImage(url: string,
                                body: FormData | object): Promise<GiftCenterCarouselImage> {
    return this.apiService.patchToAPI<GiftCenterCarouselImage>(
      url,
      body,
      body instanceof FormData
    );
  }

  deleteGiftCenterCarouselImage(url: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      url
    ).then();
  }

  setGiftCenterCarouselImageOrder(ids: number[]): Promise<GiftCenterCarouselImage[]> {
    return this.apiService.postToAPI<GiftCenterCarouselImage[]>(
      'gift-center-carousel-images/set-order/',
      {ids}
    );
  }

  getGiftCenterFeaturedGifts(): Promise<GiftCenterFeaturedGift[]> {
    return this.apiService.getFromAPI<GiftCenterFeaturedGift[]>(
      'gift-center-featured-gifts/'
    );
  }

  setGiftCenterFeaturedGifts(rows: GiftProductTarget[]): Promise<GiftCenterFeaturedGift[]> {
    return this.apiService.putToAPI<GiftCenterFeaturedGift[]>(
      environment.api_url + 'gift-center-featured-gifts/',
      rows.map(row => ({
        product_type: row.product_type,
        membership_id: row.membership_id,
        entry_pass_id: row.entry_pass_id,
        voucher_pack_id: row.voucher_pack_id,
      }))
    );
  }

  getGiftProductPresentations(): Promise<GiftProductPresentation[]> {
    return this.apiService.getFromAPI<GiftProductPresentation[]>(
      'gift-product-presentations/'
    );
  }

  setGiftProductPresentations(rows: GiftProductPresentationWrite[]): Promise<GiftProductPresentation[]> {
    return this.apiService.putToAPI<GiftProductPresentation[]>(
      environment.api_url + 'gift-product-presentations/',
      rows.map(row => ({
        product_type: row.product_type,
        membership_id: row.membership_id,
        entry_pass_id: row.entry_pass_id,
        voucher_pack_id: row.voucher_pack_id,
        tag_text: row.tag_text,
        tag_color_hex: row.tag_color_hex,
        short_description: row.short_description,
      }))
    );
  }

  createGiftCenterHomePreview(payload: GiftCenterHomePreviewPayload): Promise<GiftCenterHomePreview> {
    return this.apiService.postToAPI<GiftCenterHomePreview>(
      'gift-center-home-previews/',
      {
        gift_center_alert_enabled: payload.gift_center_alert_enabled,
        gift_center_alert_text: payload.gift_center_alert_text,
        gift_center_alert_color_hex: payload.gift_center_alert_color_hex,
        gift_center_alert_start_date: payload.gift_center_alert_start_date,
        gift_center_alert_end_date: payload.gift_center_alert_end_date,
        gift_center_carousel_title: payload.gift_center_carousel_title,
        featured_gifts: payload.featured_gifts.map(row => ({
          product_type: row.product_type,
          membership_id: row.membership_id,
          entry_pass_id: row.entry_pass_id,
          voucher_pack_id: row.voucher_pack_id,
        })),
        presentations: payload.presentations.map(row => ({
          product_type: row.product_type,
          membership_id: row.membership_id,
          entry_pass_id: row.entry_pass_id,
          voucher_pack_id: row.voucher_pack_id,
          tag_text: row.tag_text,
          tag_color_hex: row.tag_color_hex,
          short_description: row.short_description,
        })),
      }
    );
  }
}
